import { useDraggable } from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

interface HandCardProps {
  instance: CardInstance;
  /** True when this card is the currently hovered one in a fanned hand. */
  isHovered?: boolean;
  /**
   * Right-margin in px. Negative for overlap in a fanned hand.
   * 0 for the last card (no trailing gap needed).
   */
  marginRight?: number;
  onHover?: (instance: CardInstance | null) => void;
}

export default function HandCard({ instance, isHovered, marginRight, onHover }: HandCardProps) {
  const { instanceId, card } = instance;

  const selectedInstanceIds = usePlaytesterStore((s) => s.selectedInstanceIds);
  const toggleSelection = usePlaytesterStore((s) => s.toggleSelection);
  const setSelection = usePlaytesterStore((s) => s.setSelection);
  const setContextMenu = usePlaytesterStore((s) => s.setContextMenu);
  const setPersistentPreview = usePlaytesterStore((s) => s.setPersistentPreview);

  const isSelected = selectedInstanceIds.has(instanceId);
  // Use 'large' (672×936) for crisp rendering at high zoom; falls back to 'normal'.
  const artUri = getCardImageUri(card, 'large');

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: instanceId,
    data: { instanceId, zone: 'hand' },
  });

  function handleClick(e: React.MouseEvent) {
    if (e.shiftKey) {
      toggleSelection(instanceId);
    } else {
      setSelection([instanceId]);
    }
  }

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, instanceId, zone: 'hand' });
  }

  function handleDoubleClick() {
    setPersistentPreview(card);
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={() => onHover?.(instance)}
      onMouseLeave={() => onHover?.(null)}
      className={`relative cursor-pointer select-none shrink-0 rounded-sm overflow-hidden border transition-all duration-150 ${
        isSelected
          ? 'border-orange-500 ring-2 ring-orange-500'
          : 'border-gray-700 hover:border-gray-500'
      } ${isDragging ? 'opacity-40' : ''}`}
      style={{
        width: 'var(--pt-card-w)',
        height: 'var(--pt-card-h)',
        // Spacing: controls the fan gap / overlap between cards
        marginRight: marginRight !== undefined ? `${marginRight}px` : undefined,
        // Hovered card pops up above its neighbours
        zIndex: isHovered ? 50 : undefined,
        transform: isHovered ? 'translateY(-12px) scale(1.06)' : undefined,
      }}
    >
      {artUri ? (
        <img src={artUri} alt={card.name} className="w-full h-full object-cover" draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
      ) : (
        <div className="w-full h-full bg-gray-800 flex items-center justify-center p-0.5">
          <span className="text-gray-400 text-[8px] text-center leading-tight">{card.name}</span>
        </div>
      )}
    </div>
  );
}
